import React, { useRef, useState } from 'react';
import { Box, Button, Typography, Stack, IconButton, Chip, Paper, Tooltip, SxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { CloudUpload, Delete, InsertDriveFile, Image, PictureAsPdf } from '@mui/icons-material';

interface DocumentUploadProps {
  value?: string | File; // doc_url or File object
  onChange: (file: File | null) => void;
  accept?: string;
  label?: string;
  disabled?: boolean;
  containerSx?: SxProps<Theme>;
}

/**
 * DocumentUpload - handles file selection, returns File object via onChange
 *
 * Props:
 * - value: current doc_url (string) or File object
 * - onChange: function(file: File | null) => void
 * - accept: accepted file types (default: images and pdf)
 * - label: label for upload button
 * - disabled: disables input
 */
const DocumentUpload: React.FC<DocumentUploadProps> = ({ 
  value, 
  onChange, 
  accept = 'image/*,application/pdf', 
  label = 'Upload Document', 
  disabled,
  containerSx
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Update preview URL when value changes
  React.useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (typeof value === 'string') {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      // Pass the File object directly to parent
      onChange(file);
    } catch (err) {
      setError('Failed to select file.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
    setPreviewUrl(null);
  };

  const getFileName = () => {
    if (value instanceof File) return value.name;
    if (typeof value === 'string') return value.split('/').pop() || 'Document';
    return null;
  };

  const getFileIcon = () => {
    if (value instanceof File) {
      if (value.type.startsWith('image/')) return <Image fontSize="small" />;
      if (value.type === 'application/pdf') return <PictureAsPdf fontSize="small" />;
    } else if (typeof value === 'string') {
      if (value.match(/\.(jpg|jpeg|png|gif)$/i)) return <Image fontSize="small" />;
      if (value.endsWith('.pdf')) return <PictureAsPdf fontSize="small" />;
    }
    return <InsertDriveFile fontSize="small" />;
  };

  const isImage = () => {
    if (value instanceof File) return value.type.startsWith('image/');
    if (typeof value === 'string') return value.match(/\.(jpg|jpeg|png|gif)$/i);
    return false;
  };

  return (
    <Box sx={{ width: '100%', ...(containerSx as object) }}>
      {!value ? (
        <Paper
          sx={{
            border: '2px dashed',
            borderColor: error ? 'error.main' : 'primary.light',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: 'background.default',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={disabled || uploading}
          />
          <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {label}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            sx={{ textTransform: 'none' }}
          >
            {uploading ? 'Selecting...' : 'Select File'}
          </Button>
          {error && (
            <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
              {error}
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            bgcolor: 'background.paper'
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: 'primary.light',
                color: 'primary.main'
              }}
            >
              {getFileIcon()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }} noWrap>
                {getFileName()}
              </Typography>
              <Chip 
                label="Uploaded" 
                size="small" 
                color="success" 
                sx={{ height: 20, mt: 0.5 }}
              />
            </Box>
            <Stack direction="row" spacing={0.5}>
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={disabled || uploading}
              />
              <Tooltip title="Replace">
                <IconButton
                  onClick={() => inputRef.current?.click()}
                  size="small"
                  disabled={disabled || uploading}
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'primary.light' }
                  }}
                >
                  <CloudUpload fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  onClick={handleRemove}
                  size="small"
                  disabled={disabled || uploading}
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'error.light' }
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          
          {/* Preview */}
          {previewUrl && isImage() && (
            <Box mt={1.5} sx={{ textAlign: 'center' }}>
              <img 
                src={previewUrl} 
                alt="Preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 150, 
                  borderRadius: 8,
                  border: '1px solid #e0e0e0'
                }} 
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default DocumentUpload;
